import React, { useState, useEffect, useRef } from "react";
import {
  FaPlus,
  FaKey,
  FaCheckCircle,
  FaTrash,
  FaChevronRight,
  FaChevronLeft,
  FaFileImport,
  FaMinusCircle,
  FaTimesCircle,
} from "react-icons/fa";
import { FaUsers, FaUserPlus } from "react-icons/fa6";
import { AiOutlineStop } from "react-icons/ai";
import { IoMdArrowDropdown } from "react-icons/io";
import { searchAllStateUsers, searchChannels, searchTeams } from "../api/searchApi";
import dayjs from "dayjs";
import { IoCloseSharp, IoSearchSharp } from "react-icons/io5";
import PerfectScrollbar from "react-perfect-scrollbar";
import Swal from "sweetalert2";
import { activeUser, changeUserPosition, changeUsersPassword, createUsers, getUserDetail } from "../api/userApi";
import { addTeamUsers } from "../api/teamApi";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { ImExit } from "react-icons/im";
import Pagination from "../components/molecules/Pagination";
import SearchHistoryModal from "../components/molecules/searchHistoryModal";
import { addSearchHistory, getSearchHistory, removeSearchHistory } from "../utils/store/localStorage";
import SortButton from "../components/molecules/sortButton";
import { RiFileExcel2Fill } from "react-icons/ri";

const validateEmail = (email: string) => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

interface UserRow {
  localId: number;
  email: string;
  nickname: string;
  password: string;
  userId: string;
  username?: string;
  createSuccess: boolean;
  createMessage?: string;
  teamAddSuccess: boolean;
  teamAddMessage?: string;
}

interface TeamInfo {
  id: string;
  displayName: string;
  name: string;
  deleteAt: number;
  createAt: number;
  updateAt: number;
  memberCount: number;
}

interface ChannelInfo {
  id: string;
  displayName: string;
  name: string;
  totalMsgCount: number;
  lastPostAt: number;
  type: string;
  memberCount: number;
  deleteAt: number;
}

interface UserInfo {
  id: string;
  username: string;
  nickname: string;
  email: string;
  position: string;
  deleteAt: number;
  failedAttempts: number;
  lastLogin: number;
}

interface PageInfo {
  size: number;
  number: number;
  totalElements: number;
  totalPages: number;
}

interface UserDetailInfo {
  id: string;
  createAt: number;
  updateAt: number;
  deleteAt: number;
  username: string;
  email: string;
  nickname: string;
  lastLogin: number;
  teams: {
    teamId: string;
    teamName: string;
  }[];
  files: {
    id: string;
    name: string;
    extension: string;
    size: number;
    mimeType: string;
    createAt: number;
    teamId: string;
    teamName: string;
    channelId: string;
  }[];
  totalFileSize: number;
}

const UserList: React.FC = () => {
  const scrollbarRef = useRef<HTMLElement | null>(null);
  const [teams, setTeams] = useState<TeamInfo[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string>("");
  const [channels, setChannels] = useState<ChannelInfo[]>([]);
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [currentPage, setCurrentPage] = useState(0); // 현재 페이지
  const [currentSize, setCurrentSize] = useState(10); // 현재 사이즈
  const [selectedChannel, setSelectedChannel] = useState<string>("");
  const [pageInfo, setPageInfo] = useState<PageInfo>({
    size: 10,
    number: 0,
    totalElements: 0,
    totalPages: 0,
  });
  const [keyword, setKeyword] = useState<string>("");
  const [position, setPosition] = useState<string>("");
  const [lockedOnly, setLockedOnly] = useState<string>("");
  const [deletedOnly, setDeletedOnly] = useState<string>("false");

  const [allUserCheck, setAllUserCheck] = useState<boolean>(false);
  const [selectedUsers, setSeletecUsers] = useState<string[]>([]);

  // 클릭 표시
  const [selectedRow, setSelectedRow] = useState<string>("");

  // 정렬 기준
  const [sortState, setSortState] = useState<string>("");
  const teamSearchRef = useRef<HTMLDivElement>(null);
  const modalTeamSearchRef = useRef<HTMLDivElement>(null);

  const [isTeamSearch, setIsTeamSearch] = useState<boolean>(false);
  const [teamSearchKeyword, setTeamSearchKeyword] = useState<string>("");
  const [filteredTeams, setFilteredTeams] = useState<TeamInfo[]>([]);

  const [isModalTeamSearch, setIsModalTeamSearch] = useState<boolean>(false);
  const [modalTeamSearchKeyword, setModalTeamSearchKeyword] = useState<string>("");
  const [modalFilteredTeams, setModalFilteredTeams] = useState<TeamInfo[]>([]);

  const handleSortState = (state: string) => {
    setSortState(state);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (teamSearchRef.current && !teamSearchRef.current.contains(event.target as Node)) {
        setIsTeamSearch(false);
      }
      if (modalTeamSearchRef.current && !modalTeamSearchRef.current.contains(event.target as Node)) {
        setIsModalTeamSearch(false);
      }
    };

    if (isTeamSearch || isModalTeamSearch) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isTeamSearch, isModalTeamSearch]);

  const handleSelectedRow = (id: string) => {
    setSelectedRow(id);
  };

  //팀 목록 불러오기 및 선택
  const searchAllTeams = async () => {
    const data = await searchTeams(0, 9999, new Date(0).getTime(), new Date().getTime(), "", "false", "");
    if (data) {
      setTeams(data.data.content);
      setFilteredTeams(data.data.content);
      console.log(data.data);
    }
  };

  useEffect(() => {
    if (teamSearchKeyword) {
      const filtered = teams.filter((team) =>
        team.displayName.toLowerCase().includes(teamSearchKeyword.toLowerCase())
      );
      setFilteredTeams(filtered);
    } else {
      setFilteredTeams(teams);
    }
  }, [teamSearchKeyword, teams]);

  useEffect(() => {
    if (modalTeamSearchKeyword) {
      const filtered = teams.filter((team) =>
        team.displayName.toLowerCase().includes(modalTeamSearchKeyword.toLowerCase())
      );
      setModalFilteredTeams(filtered);
    } else {
      setModalFilteredTeams(teams);
    }
  }, [modalTeamSearchKeyword, teams]);

  const handleTeamSelect = (teamId: string) => {
    setSelectedTeam(teamId);
    const selected = teams.find((t) => t.id === teamId);
    setTeamSearchKeyword(selected ? selected.displayName : "");
    setIsTeamSearch(false);
  };

  // 채널 목록 불러오기 및 선택
  useEffect(() => {
    setSelectedChannel("");
    if (selectedTeam) {
      console.log(selectedTeam);
      channelsSearch();
    } else {
      setChannels([]);
    }
    allStateUsersSearch();
    if (scrollbarRef.current) {
      scrollbarRef.current.scrollTop = 0; // 스크롤 맨 위로 이동
    }
  }, [selectedTeam]);

  const channelsSearch = async () => {
    if (selectedTeam) {
      const data = await searchChannels(selectedTeam);
      if (data) {
        setChannels(data.data.content);
        console.log(data.data);
      }
    }
  };

  // 유저 역할 변경
  const positionChange = async (user: UserInfo, position: string) => {
    if (position === "운영자") {
      const data = await changeUserPosition(user.id, "OPERATOR");
      if (data) {
        console.log(data.data);
        allStateUsersSearch();
      }
    } else if (position === "강사") {
      const data = await changeUserPosition(user.id, "TEACHER");
      if (data) {
        console.log(data.data);
        allStateUsersSearch();
      }
    } else if (position === "교육생") {
      const data = await changeUserPosition(user.id, "NONE");
      if (data) {
        console.log(data.data);
        allStateUsersSearch();
      }
    }
  };

  //검색 조건 상세하게 설정한 유저 검색
  const allStateUsersSearch = async (isSearch: boolean = false) => {
    const data = await searchAllStateUsers(
      selectedTeam,
      selectedChannel,
      isSearch ? 0 : currentPage,
      currentSize,
      keyword,
      position,
      lockedOnly,
      deletedOnly,
      sortState
    );

    if (data) {
      console.log(data.data);
      setUsers(data.data.content);
      setPageInfo(data.data.page);
      setCurrentPage(data.data.page.number);
      setCurrentSize(data.data.page.size);
      setSeletecUsers([]);
      setAllUserCheck(false);
      if (data.data.content.length > 0) {
        addSearchHistory(keyword.trim(), "user");
      }
    }
  };

  const handleAllUserCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAllUserCheck(event.target.checked); // 현재 체크 상태 업데이트
  };
  // 유서 선택 추가 함수
  const choiceUserAdd = (userId: string) => {
    if (userId && !selectedUsers.includes(userId)) {
      if (selectedUsers.length === users.length - 1) {
        setAllUserCheck(true);
        return;
      }
      setSeletecUsers([...selectedUsers, userId]); // 새로운 채널을 리스트에 추가
    }
  };

  // 유저 선택 취소 함수
  const choiceUserRemove = (userId: string) => {
    setAllUserCheck(false);
    setSeletecUsers(selectedUsers.filter((ch) => ch !== userId)); // 삭제할 채널을 제외하고 리스트 업데이트
  };

  useEffect(() => {
    if (allUserCheck) {
      setSeletecUsers(users.map((user) => user.id));
    } else {
      if (selectedUsers.length === users.length) {
        setSeletecUsers([]);
      }
    }
  }, [allUserCheck]);

  // 유저 비활성화 활성화 경고창
  const handleConfirm = (user: UserInfo, activeState: boolean) => {
    Swal.fire({
      title: `${user.nickname} (${user.email}) 유저를 정말 \n${
        user.deleteAt === 0 ? "비활성화" : "활성화"
      } 시키시겠습니까?`,
      // text: "이 작업은 되돌릴 수 없습니다.",
      icon: "warning",
      showCancelButton: true, // '아니오' 버튼 추가
      confirmButtonColor: "#3085d6", // '예' 버튼 색상
      cancelButtonColor: "#d33", // '아니오' 버튼 색상
      confirmButtonText: "예",
      cancelButtonText: "아니오",
    }).then((result) => {
      if (result.isConfirmed) {
        // '예'를 선택한 경우 실행할 함수
        userActive([user.id], activeState);
      } else {
        // '아니오'를 선택한 경우 실행할 코드
      }
    });
  };

  // 여러유저 비활성화 활성화 경고창
  const handleUsersConfirm = (activeState: boolean) => {
    Swal.fire({
      title: `선택한 유저들을 정말 \n${!activeState ? "비활성화" : "활성화"} 시키시겠습니까?`,
      // text: "이 작업은 되돌릴 수 없습니다.",
      icon: "warning",
      showCancelButton: true, // '아니오' 버튼 추가
      confirmButtonColor: "#3085d6", // '예' 버튼 색상
      cancelButtonColor: "#d33", // '아니오' 버튼 색상
      confirmButtonText: "예",
      cancelButtonText: "아니오",
    }).then((result) => {
      if (result.isConfirmed) {
        // '예'를 선택한 경우 실행할 함수
        userActive(selectedUsers, activeState);
      } else {
        // '아니오'를 선택한 경우 실행할 코드
      }
    });
  };

  const userActive = async (userId: string[], activeState: boolean) => {
    // 원하는 동작을 여기에 추가
    const data = await activeUser(userId, activeState);

    if (data) {
      allStateUsersSearch();
    }
  };

  // n개씩 보기 드롭다운 핸들러
  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setCurrentSize(Number(event.target.value));
    // setPageInfo((prev) => ({ ...prev, size: number(event.target.value) }));
  };

  /* 검색하기 */
  const handleKeyword = (keyword: string) => {
    setKeyword(keyword);
  };
  //검색창 초기화
  const searchReset = () => {
    setKeyword("");
    setPosition("");
    setLockedOnly("");
    setDeletedOnly("false");
    setChannels([]);
    setSelectedChannel("");
    setSelectedTeam("");
    setTeamSearchKeyword("");
  };

  // 역할 드롭다운 핸들러
  const handlePosition = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setPosition(event.target.value);
  };
  // 상태 드롭다운 핸들러
  const handleLockedOnly = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setLockedOnly(event.target.value);
  };
  // 잠김 드롭다운 핸들러
  const handleDeletedOnly = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setDeletedOnly(event.target.value);
  };
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      allStateUsersSearch(true);
    } else if (e.ctrlKey && e.key === " ") {
      handleSearchHistoryModal(true);
    }
  };

  // 페이지 네이션
  const handlePageChange = (page: number) => {
    if (page >= 0 && page < pageInfo.totalPages) {
      setCurrentPage(page);
      // setPageInfo((prev) => ({ ...prev, number: page }));
    }
  };

  // 검색창 최신 기록 모달 관련
  const inputRef = useRef<HTMLInputElement>(null);
  const [isSearchHistoryModal, setIsSearchHistoryModal] = useState<boolean>(false);
  const [items, setItems] = useState<string[]>([]);

  const handleSearchHistoryModal = (state: boolean) => {
    setIsSearchHistoryModal(state);
  };

  const removeSearchUserHistory = (item: string) => {
    removeSearchHistory(item, "user");
    setItems(getSearchHistory("user"));
  };

  useEffect(() => {
    searchAllTeams();
    setItems(getSearchHistory("user"));

    const handleKeyDown = (event: KeyboardEvent) => {
      // '/' 키 입력 감지 (input에 포커스가 없을 때만 작동)
      if (event.key === "/" && document.activeElement !== inputRef.current) {
        event.preventDefault(); // '/' 입력을 방지
        inputRef.current?.focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (currentPage === 0) {
      allStateUsersSearch();
    }
    setCurrentPage(0);
    if (scrollbarRef.current) {
      scrollbarRef.current.scrollTop = 0; // 스크롤 맨 위로 이동
    }
  }, [currentSize]);

  useEffect(() => {
    allStateUsersSearch();
    if (scrollbarRef.current) {
      scrollbarRef.current.scrollTop = 0; // 스크롤 맨 위로 이동
    }
  }, [currentPage]);

  useEffect(() => {
    allStateUsersSearch();
    if (scrollbarRef.current) {
      scrollbarRef.current.scrollTop = 0; // 스크롤 맨 위로 이동
    }
  }, [sortState, position, deletedOnly, lockedOnly, selectedChannel]);

  //  사용자 추가 모달 관련 !!!!
  //  사용자 추가 모달 관련 !!!!
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [overallPassword, setOverallPassword] = useState("");
  const [addModalUsers, setAddModalUsers] = useState<UserRow[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [userAddModal, setUserAddModal] = useState<boolean>(false);
  const [userIdx, setUserIdx] = useState<number>(0);
  const userAddScrollbarRef = useRef<HTMLElement | null>(null);

  const handleUserAddModal = () => {
    allStateUsersSearch();
    setUserAddModal(!userAddModal);
    setAddModalUsers([]);
    const initialTeamId = selectedTeam;
    setSelectedTeamId(initialTeamId);
    const initialTeam = teams.find((t) => t.id === initialTeamId);
    setModalTeamSearchKeyword(initialTeam ? initialTeam.displayName : "");
    setModalFilteredTeams(teams);
    setOverallPassword("");
    setUserIdx(0);
  };

  const handleAllPassword = () => {
    const changeAll = addModalUsers.map((user) => (user.createSuccess ? user : { ...user, password: overallPassword }));
    console.log(changeAll);

    setAddModalUsers(changeAll);
  };

  // 새 행 추가
  const handleAddRow = () => {
    const newRow: UserRow = {
      localId: userIdx,
      email: "",
      nickname: "",
      password: "",
      userId: "",
      username: "",
      createSuccess: false,
      createMessage: "",
      teamAddSuccess: false,
      teamAddMessage: "",
    };
    setUserIdx(userIdx + 1);
    setAddModalUsers((prev) => [...prev, newRow]);

    setTimeout(() => {
      if (userAddScrollbarRef.current) {
        userAddScrollbarRef.current.scrollTop = userAddScrollbarRef.current.scrollHeight + 10; // 스크롤 맨 아래로 이동
      }
    }, 0);
  };

  // 행 삭제
  const handleDeleteRow = (localId: number) => {
    setAddModalUsers((prev) => prev.filter((u) => u.localId !== localId));
  };

  // 계정 생성 버튼 활성화 조건
  const canEnableCreateButton = (user: UserRow): boolean => {
    // 이미 계정 생성 성공했다면 비활성화
    if (user.createSuccess) return false;
    // 닉네임과 이메일이 모두 채워져야 함
    if (!user.nickname.trim()) return false;
    if (!validateEmail(user.email)) return false;
    // 사용자 비번 또는 overallPassword 중 하나가 5글자 이상이어야 함
    const userPwd = user.password.trim();
    // const overallPwd = overallPassword.trim();
    if (userPwd) {
      if (userPwd.length < 5) return false;
    } else {
      return false;
    }
    return true;
  };

  // 단건 계정 생성
  const handleCreateUser = async (user: UserRow) => {
    if (user.createSuccess) return;
    const payload = [
      {
        email: user.email,
        nickname: user.nickname,
        password: user.password,
      },
    ];

    const data = await createUsers(payload);

    if (data) {
      const result = data.data.results[0];
      console.log(result);

      if (result.success) {
        setAddModalUsers((prev) =>
          prev.map((u) => {
            if (u.localId === user.localId) {
              return {
                ...u,
                createSuccess: result.success,
                userId: result.userId,
                username: result.username,
                createMessage: result.message,
              };
            }
            return u;
          })
        );
      } else {
        setAddModalUsers((prev) =>
          prev.map((u) =>
            u.localId === user.localId
              ? {
                  ...u,
                  createSuccess: false,
                  createMessage: result.message || "계정 생성 실패",
                }
              : u
          )
        );
      }
    }
  };

  // 단건 팀 등록
  const handleTeamRegister = async (user: UserRow) => {
    // 팀 선택 안 됐으면 중단
    if (!selectedTeamId) return;
    // 계정이 생성 성공된 상태가 아니면 중단
    if (!user.createSuccess) return;
    // DB userId가 없으면 중단
    if (!user.userId) return;

    const teamIds = [selectedTeamId];
    const userIds = [user.userId];

    const data = await addTeamUsers(teamIds, userIds);

    if (data) {
      const result = data.data[0];
      console.log(data.data);

      setAddModalUsers((prev) =>
        prev.map((u) =>
          u.localId === user.localId
            ? {
                ...u,
                teamAddSuccess: result.success,
                teamAddMessage: result.message,
              }
            : u
        )
      );
    }
  };

  // 전체 계정 생성
  const canEnableCreateAll = () => {
    // (created && createSuccess)가 아닌 사용자들만 검사
    const notCreatedUsers = addModalUsers.filter((u) => !u.createSuccess);
    if (notCreatedUsers.length === 0) return false;

    for (let u of notCreatedUsers) {
      if (!u.nickname.trim()) return false;
      if (!validateEmail(u.email)) return false;
      const userPwd = u.password.trim();
      // const overallPwd = overallPassword.trim();
      if (userPwd) {
        if (userPwd.length < 5) return false;
      } else {
        return false;
      }
    }
    return true;
  };

  const handleCreateAll = async () => {
    // 이미 계정 생성 성공한 유저는 제외하고, 실패했거나 미시도인 유저만 요청
    const notCreatedUsers = addModalUsers.filter((u) => !u.createSuccess);
    if (notCreatedUsers.length === 0) return;

    const payload = notCreatedUsers.map((u) => {
      return {
        email: u.email,
        nickname: u.nickname,
        password: u.password,
      };
    });

    const data = await createUsers(payload);

    if (data) {
      const result = data.data.results;
      console.log(result);

      setAddModalUsers((prev) =>
        prev.map((u) => {
          if (u.createSuccess) return u; // 이미 성공한 사용자 제외
          const r = result.find((r: any) => r.email === u.email);
          if (r.success) {
            return {
              ...u,
              createSuccess: r.success,
              userId: r.userId,
              username: r.username,
              createMessage: r.message,
            };
          }
          return {
            ...u,
            createSuccess: false,
            createMessage: r.message || "계정 생성 실패",
          };
        })
      );
    }
  };

  // 엑셀/CSV 등록 버튼
  const handleFileButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  // 파일 업로드 핸들러
  const handleFileImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isExcel = file.name.endsWith(".xlsx") || file.name.endsWith(".xls");

    if (isExcel) {
      const reader = new FileReader();
      reader.onload = (evt) => {
        if (!evt.target?.result) return;
        const data = new Uint8Array(evt.target.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const csvData = XLSX.utils.sheet_to_csv(worksheet, {
          FS: ",",
          RS: "\n",
          strip: false,
        });
        Papa.parse(csvData, {
          complete: (results) => {
            const parsedRows = results.data as string[][];
            appendUsersFromRows(parsedRows);
          },
          error: (err) => {
            console.error("CSV 파싱 에러:", err);
          },
        });
      };
      reader.readAsArrayBuffer(file);
    } else {
      // CSV 파일
      Papa.parse(file, {
        complete: (results) => {
          const parsedRows = results.data as string[][];
          appendUsersFromRows(parsedRows);
        },
        error: (err) => {
          console.error("CSV 파싱 에러:", err);
        },
      });
    }
  };

  const excelFailAlert = () => {
    Swal.fire({
      title: "실패",
      text: "엑셀 양식이 일치하지 않습니다.",
      icon: "error", // success, error, warning, info, question 중 선택 가능
      confirmButtonText: "확인",
      timer: 3000, // 알림이 자동으로 사라지는 시간 (ms)
      // timerProgressBar: true, // 타이머 진행 상태를 표시
    });
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = "/example/student_example.xlsx"; // public 폴더 내 경로
    link.download = "student_example.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 파싱된 row 데이터를 users state에 추가
  const appendUsersFromRows = (parsedRows: string[][]) => {
    const newUsers: UserRow[] = [];
    parsedRows.some((row, idx) => {
      // row[0] = 닉네임, row[1] = 이메일
      if (Array.isArray(row) && row.length >= 2) {
        if (idx === 0 && row[0].trim() !== "교육생" && row[1].trim() !== "이메일") {
          excelFailAlert();
          return true;
        }
        if (idx === 0 && row[0].trim() === "교육생" && row[1].trim() === "이메일") {
          return false;
        }

        const nickname = String(row[0] || "").trim();
        const email = String(row[1] || "").trim();
        if (nickname || email) {
          newUsers.push({
            localId: userIdx + idx,
            email,
            nickname,
            password: "",
            userId: "",
            username: "",
            createSuccess: false,
            createMessage: "",
            teamAddSuccess: false,
            teamAddMessage: "",
          });
        }
      }
    });
    setUserIdx(userIdx + parsedRows.length);
    setAddModalUsers((prev) => [...prev, ...newUsers]);
  };

  // 사용자 추가할 때 다른 팀을 선택하면 팀 등록 여부 초기화
  useEffect(() => {
    const teamAddStateReset = addModalUsers.map((user) => ({ ...user, teamAddSuccess: false, teamAddMessage: "" }));

    setAddModalUsers(teamAddStateReset);
  }, [selectedTeamId]);

  // 전체 팀 생성 가능성성
  const canEnableRegisterAllTeam = () => {
    // 팀이 선택되지 않았다면 불가
    if (!selectedTeamId) return false;
    // 이미 팀 등록까지 완료( teamRegistered && teamSuccess )된 사용자 제외
    const notRegisterUsers = addModalUsers.filter((u) => u.createSuccess && !u.teamAddSuccess);
    if (notRegisterUsers.length === 0) return false;

    return true;
  };

  // 팀 일괄 등록
  const handleTeamRegisterAll = async () => {
    // 팀이 선택되지 않았다면 불가
    if (!selectedTeamId) return;
    // 이미 팀 등록까지 완료( teamRegistered && teamSuccess )된 사용자 제외
    const targetUsers = addModalUsers.filter((u) => u.createSuccess && !u.teamAddSuccess);
    if (targetUsers.length === 0) return;

    const teamIds = [selectedTeamId];
    const userIds = targetUsers.map((u) => u.userId);

    const data = await addTeamUsers(teamIds, userIds);

    if (data) {
      const results = data.data;
      console.log(data.data);

      setAddModalUsers((prev) =>
        prev.map((u) => {
          const matched = results.find((d: any) => d.userId === u.userId);
          if (matched) {
            return {
              ...u,
              teamAddSuccess: matched.success,
              teamAddMessage: matched.message,
            };
          }
          return u;
        })
      );
    }
  };

  // 사용자 추가 모달 끝!!!

  // 패스워드 모달 관련 시작!!
  // 패스워드 모달 관련 시작!!
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [passwordModal, setPasswordModal] = useState<boolean>(false);

  // 사용자 상세 정보 모달 관련 시작!!
  // 사용자 상세 정보 모달 관련 시작!!
  const [userDetailModal, setUserDetailModal] = useState<boolean>(false);
  const [userDetail, setUserDetail] = useState<UserDetailInfo | null>(null);
  const [loadingDetail, setLoadingDetail] = useState<boolean>(false);

  const usersPasswordChange = async () => {
    const data = await changeUsersPassword(selectedUsers, newPassword);

    if (data) {
      console.log(data.data);

      passwordChangeSuccessAlert();
    }
  };

  const passwordChangeSuccessAlert = () => {
    Swal.fire({
      title: "성공",
      text: "비밀번호가 성공적으로 설정되었습니다.",
      icon: "success", // success, error, warning, info, question 중 선택 가능
      confirmButtonText: "확인",
      timer: 3000, // 알림이 자동으로 사라지는 시간 (ms)
      // timerProgressBar: true, // 타이머 진행 상태를 표시
    });
    handlePasswordModal();
  };

  const handlePasswordModal = () => {
    setPasswordModal(!passwordModal);
    setNewPassword("");
    setError("");
    setConfirmPassword("");
  };

  const handlePasswordChange = () => {
    if (newPassword.length < 5) {
      setError("비밀번호는 최소 5자 이상이어야 합니다.");
    } else if (newPassword !== confirmPassword) {
      setError("비밀번호가 서로 다릅니다.");
    } else {
      setError(""); // 에러 메시지 초기화
      usersPasswordChange();
    }
  };
  // 패스워드 모달 관련 끝!!
  // 패스워드 모달 관련 끝!!

  // 사용자 상세 정보 조회
  const fetchUserDetail = async (userId: string) => {
    setLoadingDetail(true);
    try {
      const data = await getUserDetail(userId);
      if (data && data.success) {
        setUserDetail(data.data);
        setUserDetailModal(true);
      } else {
        Swal.fire({
          title: "오류",
          text: "사용자 상세 정보를 불러올 수 없습니다.",
          icon: "error",
          confirmButtonText: "확인",
        });
      }
    } catch (error) {
      console.error("사용자 상세 정보 조회 실패:", error);
      Swal.fire({
        title: "오류",
        text: "사용자 상세 정보를 불러오는 중 오류가 발생했습니다.",
        icon: "error",
        confirmButtonText: "확인",
      });
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleUserDetailModal = () => {
    setUserDetailModal(!userDetailModal);
    if (!userDetailModal) {
      setUserDetail(null);
    }
  };

  // 파일 크기 포맷팅 함수
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };
  // 사용자 상세 정보 모달 관련 끝!!
  // 사용자 상세 정보 모달 관련 끝!!

  return (
    <div
      className="min-h-[80vh] max-h-[80vh] bg-gray-50"
      onClick={() => {
        handleSearchHistoryModal(false);
      }}
    >
      <div className="max-w-full px-4 py-4 mx-auto sm:px-2 lg:px-4">
        <div className="p-6 mb-6 bg-white rounded-lg shadow-sm">
          {/* 유저 검색 */}
          <div className="mt-5">
            <div className="mb-5">
              <div className="flex flex-col mb-3 space-y-3">
                <div className="flex items-center space-x-4">
                  <div className="flex-1">
                    <div className="relative flex">
                      <input
                        type="text"
                        placeholder="사용자 검색"
                        value={keyword}
                        onChange={(e) => handleKeyword(e.target.value)}
                        className="w-full py-2 pl-10 pr-4 border border-gray-300 rounded-md"
                        onKeyDown={handleSearchKeyDown} // Enter 키 이벤트 처리
                        ref={inputRef}
                        onFocus={() => {
                          handleSearchHistoryModal(true);
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSearchHistoryModal(true);
                        }}
                      />
                      <SearchHistoryModal
                        items={getSearchHistory("user")}
                        isOpen={isSearchHistoryModal}
                        onClose={handleSearchHistoryModal}
                        handleKeyword={handleKeyword}
                        inputRef={inputRef}
                        removeSearchTeamHistory={removeSearchUserHistory}
                      />
                      <span className="absolute text-gray-400 left-3 top-3">
                        <IoSearchSharp size={20} />
                      </span>
                      <div className="relative w-1/2 mx-3" ref={teamSearchRef}>
                        <input
                          type="text"
                          placeholder="팀 선택"
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-custom focus:border-transparent"
                          value={teamSearchKeyword}
                          onChange={(e) => {
                            setTeamSearchKeyword(e.target.value);
                            if (!isTeamSearch) setIsTeamSearch(true);
                          }}
                          onFocus={() => setIsTeamSearch(true)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        {isTeamSearch && (
                          <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                            <li
                              className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                              onClick={() => {
                                setSelectedTeam("");
                                setTeamSearchKeyword("");
                                setIsTeamSearch(false);
                              }}
                            >
                              팀 선택
                            </li>
                            {filteredTeams.map((team) => (
                              <li
                                key={team.id}
                                className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                                onClick={() => handleTeamSelect(team.id)}
                              >
                                {team.displayName}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div className={`w-1/2 ${selectedTeam ? "visible" : "invisible"}`}>
                        <select
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-custom focus:border-transparent"
                          onChange={(e) => setSelectedChannel(e.target.value)}
                          value={selectedChannel}
                          disabled={channels.length === 0}
                        >
                          <option value="">채널 선택</option>
                          {channels.map((channel) => (
                            <option key={channel.id} value={channel.id}>
                              {channel.displayName}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="flex space-x-3">
                    <button
                      onClick={() => {
                        allStateUsersSearch(true);
                      }}
                      className="px-4 py-2 text-white bg-black rounded-md hover:bg-gray-700"
                    >
                      검색
                    </button>
                    <button
                      onClick={() => {
                        searchReset();
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      초기화
                    </button>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500">역할:</span>
                      <select
                        className="w-32 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-custom focus:border-transparent"
                        onChange={handlePosition}
                        value={position}
                      >
                        <option value="">전체</option>
                        <option value="운영자">운영자</option>
                        <option value="강사">강사</option>
                        <option value="교육생">교육생</option>
                      </select>
                      <span className="text-sm text-gray-500">상태:</span>
                      <select
                        className="w-32 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-custom focus:border-transparent"
                        onChange={handleDeletedOnly}
                        value={deletedOnly}
                      >
                        <option value="false">활성</option>
                        <option value="true">비활성</option>
                      </select>
                      <span className="text-sm text-gray-500">잠김:</span>
                      <select
                        className="w-32 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-custom focus:border-transparent"
                        onChange={handleLockedOnly}
                        value={lockedOnly}
                      >
                        <option value="">전체</option>
                        <option value="false">정상</option>
                        <option value="true">잠김</option>
                      </select>
                    </div>
                  </div>
                </div>
                {/* 유저 검색 끝 */}
              </div>
            </div>
            <div className="flex items-center mb-2 space-x-3">
              <button
                className={`flex bg-black text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-black/80`}
                onClick={() => {
                  handleUserAddModal();
                }}
              >
                <div className="mr-2">
                  <FaUserPlus size={20} />
                </div>
                사용자 추가
              </button>
              <button
                className="flex px-4 py-2 text-sm font-medium rounded-md bg-sky-100 text-sky-800 hover:bg-sky-50 disabled:bg-gray-200 disabled:text-gray-500"
                onClick={() => {
                  handlePasswordModal();
                }}
                disabled={selectedUsers.length === 0}
              >
                <div className="mr-2">
                  <FaKey size={20} />
                </div>
                비밀번호 변경
              </button>
              <button
                className="flex px-4 py-2 text-sm font-medium rounded-md bg-lime-100 text-lime-800 hover:bg-lime-50 disabled:bg-gray-200 disabled:text-gray-500"
                onClick={() => {
                  handleUsersConfirm(true);
                  setSeletecUsers([]);
                }}
                disabled={selectedUsers.length === 0}
              >
                <div className="mr-2">
                  <FaCheckCircle size={20} />
                </div>
                활성화
              </button>
              <button
                className="flex px-4 py-2 text-sm font-medium text-red-800 bg-red-100 rounded-md hover:bg-red-50 disabled:bg-gray-200 disabled:text-gray-500"
                onClick={() => {
                  handleUsersConfirm(false);
                }}
                disabled={selectedUsers.length === 0}
              >
                <div className="mr-2">
                  <AiOutlineStop size={20} />
                </div>
                비활성화
              </button>
            </div>

            <div className="flex items-center justify-between mb-0">
              <span className="text-sm text-gray-600">총 {pageInfo.totalElements}건</span>
              <div className="relative">
                <select
                  className="px-4 py-2 pr-8 text-sm bg-white border border-gray-200 rounded-lg appearance-none focus:ring-2 focus:ring-custom focus:border-transparent"
                  value={currentSize}
                  onChange={handleChange}
                >
                  <option value={10}>10개씩 보기</option>
                  <option value={20}>20개씩 보기</option>
                  <option value={50}>50개씩 보기</option>
                  <option value={pageInfo.totalElements}>전체 보기</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 pointer-events-none">
                  <IoMdArrowDropdown />
                </div>
              </div>
            </div>

            {/* 표 보여주는 것 */}
            <div>
              <PerfectScrollbar containerRef={(ref) => (scrollbarRef.current = ref)}>
                <div className="min-h-[450px] max-h-[450px]">
                  <table className="min-w-full border-collapse divide-y divide-gray-200">
                    <thead className="sticky top-0 bg-gray-50">
                      <tr className="border-b border-gray-200">
                        <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                          <input
                            type="checkbox"
                            className="border-gray-300 rounded"
                            checked={allUserCheck}
                            onChange={handleAllUserCheck}
                          />
                        </th>
                        <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-x-2">
                          <SortButton
                            title="사용자 ID"
                            sortState={sortState}
                            sortList={["", "username,asc", "username,desc"]}
                            handleState={handleSortState}
                          />
                        </th>
                        <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-x-2">
                          <SortButton
                            title="닉네임"
                            sortState={sortState}
                            sortList={["", "nickname,asc", "nickname,desc"]}
                            handleState={handleSortState}
                          />
                        </th>
                        <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-x-2">
                          <SortButton
                            title="이메일"
                            sortState={sortState}
                            sortList={["", "email,asc", "email,desc"]}
                            handleState={handleSortState}
                          />
                        </th>
                        <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-x-2">
                          <SortButton
                            title="역할"
                            sortState={sortState}
                            sortList={["", "position,asc", "position,desc"]}
                            handleState={handleSortState}
                          />
                        </th>
                        <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-x-2">
                          <SortButton
                            title="상태"
                            sortState={sortState}
                            sortList={["", "deleteAt,asc", "deleteAt,desc"]}
                            handleState={handleSortState}
                          />
                        </th>
                        <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-x-2">
                          <SortButton
                            title="잠김상태"
                            sortState={sortState}
                            sortList={["", "failedAttempts,asc", "failedAttempts,desc"]}
                            handleState={handleSortState}
                          />
                        </th>
                        <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-x-2">
                          <SortButton
                            title="최종 로그인"
                            sortState={sortState}
                            sortList={["", "lastLogin,asc", "lastLogin,desc"]}
                            handleState={handleSortState}
                          />
                        </th>
                        <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                          작업
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200 round">
                      {users.map((user) => (
                        <tr
                          className={`${selectedRow === user.id ? "bg-orange-100" : ""} cursor-pointer`}
                          key={user.id}
                          onClick={(e) => {
                            // 체크박스나 select, button 클릭 시에는 행 클릭 이벤트 무시
                            const target = e.target as HTMLElement;
                            if (
                              target.tagName === "INPUT" ||
                              target.tagName === "SELECT" ||
                              target.tagName === "BUTTON" ||
                              target.closest("button") ||
                              target.closest("select")
                            ) {
                              return;
                            }
                            fetchUserDetail(user.id);
                          }}
                        >
                          <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              className="border-gray-300 rounded"
                              checked={selectedUsers.includes(user.id)}
                              onChange={() => {
                                if (selectedUsers.includes(user.id)) {
                                  choiceUserRemove(user.id);
                                } else {
                                  choiceUserAdd(user.id);
                                }
                              }}
                            />
                          </td>
                          <td className="px-6 py-4 min-w-[10vw] max-w-[10vw] border-x-2">
                            <div className="text-sm text-gray-900 truncate">{user.username}</div>
                          </td>
                          <td className="px-6 py-4 min-w-[11vw] max-w-[11vw] border-x-2">
                            <div className="text-sm text-gray-900 truncate">{user.nickname}</div>
                          </td>
                          <td className="px-6 py-4 min-w-[12vw] max-w-[12vw] border-x-2">
                            <div className="text-sm text-gray-900 truncate">{user.email}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap border-x-2" onClick={(e) => e.stopPropagation()}>
                            <select
                              className={`text-sm border-gray-200 rounded-full py-1 px-4 w-24
                              ${user.position === "강사" ? "bg-lime-600 text-white" : ""}
                              ${user.position === "운영자" ? "bg-red-500 text-yellow-200" : ""}
                              ${user.position === "교육생" ? "bg-blue-300 text-black" : ""}`}
                              value={user.position}
                              onChange={(e) => {
                                positionChange(user, e.target.value);
                              }}
                            >
                              <option value="운영자" className="text-yellow-200 bg-red-500">
                                운영자
                              </option>
                              <option value="강사" className="text-white bg-lime-600 hover:bg-lime-300">
                                강사
                              </option>
                              <option value="교육생" className="text-black bg-blue-300">
                                교육생
                              </option>
                            </select>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap min-w-[7vw] max-w-[7vw] border-x-2" onClick={(e) => e.stopPropagation()}>
                            <span
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full hover:cursor-pointer
                              ${user.deleteAt === 0 ? "bg-green-100 text-green-800" : "text-red-500 bg-red-200"}`}
                              onClick={() => {
                                handleConfirm(user, user.deleteAt !== 0);
                              }}
                            >
                              {user.deleteAt === 0 ? "활성화" : "비활성화"}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap border-x-2">
                            <span
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                            ${user.failedAttempts < 10 ? "bg-gray-100 text-gray-800" : "bg-gray-800 text-gray-100"}`}
                            >
                              {user.failedAttempts < 10 ? "정상" : "잠김"}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap border-x-2">
                            {dayjs(new Date(user.lastLogin)).format("YYYY-MM-DD HH:mm")}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                            <button className="ml-4 text-red-600 hover:text-red-900">
                              <i className="text-red-600 fas fa-trash hover:text-red-900">
                                <FaTrash />
                              </i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </PerfectScrollbar>
            </div>
          </div>

          <div className="px-6 py-2 border-t border-gray-200">
            <div className="flex items-center justify-center">
              <Pagination
                currentPage={currentPage}
                totalPage={pageInfo.totalPages}
                handlePageChange={handlePageChange}
              />
            </div>
          </div>
        </div>
      </div>

      {/* 사용자 추가 모달 시작 !!!! */}
      <div className={`${userAddModal ? "" : "hidden"}`}>
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-60 backdrop-blur-sm"></div>
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="px-4 py-8 mx-auto max-w-8xl sm:px-6 lg:px-8">
            <div className="p-6 mb-6 bg-white rounded-lg shadow-sm">
              <div className="flex items-center justify-between gap-4">
                <div className="relative border w-72 black" ref={modalTeamSearchRef}>
                  <input
                    type="text"
                    placeholder="팀 선택"
                    className="w-full py-2 pl-3 pr-10 text-sm border-black rounded-md focus:ring-black focus:border-black"
                    value={modalTeamSearchKeyword}
                    onChange={(e) => {
                      setModalTeamSearchKeyword(e.target.value);
                      if (!isModalTeamSearch) setIsModalTeamSearch(true);
                    }}
                    onFocus={() => setIsModalTeamSearch(true)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  {isModalTeamSearch && (
                    <ul className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      <li
                        className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                        onClick={() => {
                          setSelectedTeamId("");
                          setModalTeamSearchKeyword("");
                          setIsModalTeamSearch(false);
                          setSelectedTeam("");
                          setTeamSearchKeyword("");
                        }}
                      >
                        팀 선택
                      </li>
                      {modalFilteredTeams.map((team) => (
                        <li
                          key={team.id}
                          className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                          onClick={() => {
                            setSelectedTeamId(team.id);
                            setModalTeamSearchKeyword(team.displayName);
                            setIsModalTeamSearch(false);
                            setSelectedTeam(team.id);
                            setTeamSearchKeyword(team.displayName);
                          }}
                        >
                          {team.displayName}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="relative flex items-center justify-center w-130">
                  <input
                    type="text"
                    placeholder="전체 비밀번호 입력"
                    className="w-full py-2 pl-3 pr-10 text-sm border rounded-md black"
                    value={overallPassword}
                    onChange={(e) => setOverallPassword(e.target.value)}
                  />
                  <button
                    onClick={() => {
                      handleAllPassword();
                    }}
                    className="px-4 py-2 mx-3 text-white bg-black rounded-md hover:bg-gray-700 whitespace-nowrap"
                  >
                    적용
                  </button>
                  <div className="ml-3">
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-500"
                      onClick={() => {
                        handleUserAddModal();
                      }}
                    >
                      <span className="sr-only">닫기</span>
                      <IoCloseSharp size={30} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-end">
                  <button
                    className="flex items-center justify-center px-4 py-2 mr-4 space-x-2 text-sm border-2 rounded-md shadow-sm hover:bg-gray-200 hover:font-bold"
                    onClick={handleDownload}
                  >
                    <div className="mr-1 text-green-600">
                      <RiFileExcel2Fill size={20} />
                    </div>
                    엑셀/CSV 양식
                  </button>
                  <button
                    className="flex items-center justify-center px-4 py-2 space-x-2 text-sm border-2 rounded-md shadow-sm hover:bg-gray-200 hover:font-bold"
                    onClick={handleFileButtonClick}
                  >
                    <div className="mr-1 text-green-600">
                      <RiFileExcel2Fill size={20} />
                    </div>
                    엑셀/CSV 등록
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".xlsx, .xls, .csv"
                    style={{ display: "none" }}
                    onChange={handleFileImport}
                  />
                </div>
              </div>

              {/* 사용자 추가 표 */}
              <div>
                <PerfectScrollbar containerRef={(ref) => (userAddScrollbarRef.current = ref)}>
                  <div className="whitespace-nowrap min-h-[50vh] max-h-[50vh]">
                    <table className="min-w-full divide-y divide-gray-200 table-fixed">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                            ID
                          </th>
                          <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase whitespace-nowrap">
                            사용자 ID
                          </th>
                          <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                            닉네임
                          </th>
                          <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                            이메일
                          </th>
                          <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                            비밀번호
                          </th>
                          <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                            계정 생성
                          </th>
                          <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                            팀 등록
                          </th>
                          <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                            작업
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {addModalUsers.map((user) => {
                          const isCreateBtnEnabled = canEnableCreateButton(user);
                          const disableEdit = user.createSuccess;
                          return (
                            <tr key={user.localId} className="hover:bg-gray-50">
                              <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">{user.userId ?? ""}</td>
                              <td className="px-6 py-4 overflow-hidden text-sm text-gray-900 whitespace-nowrap text-ellipsis">
                                {user.username || ""}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <input
                                  type="text"
                                  value={user.nickname}
                                  className="w-full p-2 border rounded-md black"
                                  disabled={disableEdit}
                                  onChange={(e) =>
                                    setAddModalUsers((prev) =>
                                      prev.map((u) =>
                                        u.localId === user.localId ? { ...u, nickname: e.target.value } : u
                                      )
                                    )
                                  }
                                />
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <input
                                  type="text"
                                  value={user.email}
                                  className="w-full p-2 border rounded-md black"
                                  disabled={disableEdit}
                                  onChange={(e) =>
                                    setAddModalUsers((prev) =>
                                      prev.map((u) =>
                                        u.localId === user.localId ? { ...u, email: e.target.value } : u
                                      )
                                    )
                                  }
                                />
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <input
                                  type="text"
                                  value={user.password}
                                  className="w-full p-2 border rounded-md black"
                                  disabled={disableEdit}
                                  onChange={(e) =>
                                    setAddModalUsers((prev) =>
                                      prev.map((u) =>
                                        u.localId === user.localId ? { ...u, password: e.target.value } : u
                                      )
                                    )
                                  }
                                />
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                                {user.createMessage === "" ? (
                                  <i className="text-lg text-gray-400">
                                    <FaMinusCircle />
                                  </i>
                                ) : user.createSuccess ? (
                                  <i className="text-lg text-green-500" title={user.createMessage || ""}>
                                    <FaCheckCircle />
                                  </i>
                                ) : (
                                  <i className="text-lg text-red-500 cursor-help" title={user.createMessage || ""}>
                                    <FaTimesCircle />
                                  </i>
                                )}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                                {user.teamAddMessage === "" ? (
                                  <i className="text-lg text-gray-400">
                                    <FaMinusCircle />
                                  </i>
                                ) : user.teamAddSuccess ? (
                                  <i className="text-lg text-green-500" title={user.teamAddMessage || ""}>
                                    <FaCheckCircle />
                                  </i>
                                ) : (
                                  <i className="text-lg text-red-500 cursor-help" title={user.teamAddMessage || ""}>
                                    <FaTimesCircle />
                                  </i>
                                )}
                              </td>
                              <td className="px-6 py-4 text-sm whitespace-nowrap">
                                <div className="flex gap-2">
                                  <button
                                    className={`rounded-md px-3 py-1.5 whitespace-nowrap ${
                                      isCreateBtnEnabled
                                        ? "bg-black text-white hover:bg-gray-800"
                                        : "bg-gray-100 text-gray-700 cursor-not-allowed"
                                    }`}
                                    disabled={!isCreateBtnEnabled}
                                    onClick={() => handleCreateUser(user)}
                                  >
                                    계정 생성
                                  </button>
                                  <button
                                    className={`rounded-md px-3 py-1.5 ${
                                      user.createSuccess && selectedTeamId
                                        ? "bg-black text-white hover:bg-gray-800"
                                        : "bg-gray-100 text-gray-700 cursor-not-allowed"
                                    }`}
                                    disabled={!user.createSuccess || !selectedTeamId}
                                    onClick={() => handleTeamRegister(user)}
                                  >
                                    팀 등록
                                  </button>
                                  <button
                                    className="rounded-md bg-red-100 px-3 py-1.5 text-red-700 hover:bg-red-200"
                                    onClick={() => handleDeleteRow(user.localId)}
                                    // disabled={user.created && user.createSuccess && user.teamRegistered}
                                  >
                                    <FaTrash />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </PerfectScrollbar>
              </div>
              {/* 사용자 추가 표 끝 */}

              <div className="p-4 border-t border-gray-200">
                <button
                  className="flex items-center justify-center w-full py-3 text-gray-500 border border-gray-300 border-dashed rounded-md hover:bg-gray-50"
                  onClick={handleAddRow}
                >
                  <FaPlus />
                  새로운 행 추가
                </button>
              </div>
            </div>

            <div className="flex justify-center gap-4 mt-6">
              <button
                className={`rounded-md px-8 py-3 text-lg font-medium flex items-center justify-center bg-black text-white hover:bg-gray-800`}
                onClick={() => {
                  handleUserAddModal();
                }}
              >
                <div className="mr-2">
                  <ImExit />
                </div>
                취소
              </button>
              <button
                className={`rounded-md px-8 py-3 text-lg font-medium flex items-center justify-center ${
                  canEnableCreateAll()
                    ? "bg-black text-white hover:bg-gray-800"
                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                }`}
                disabled={!canEnableCreateAll()}
                onClick={handleCreateAll}
              >
                <div className="mr-2">
                  <FaUserPlus />
                </div>
                계정 일괄 생성
              </button>
              <button
                className={`rounded-md px-8 py-3 text-lg font-medium flex items-center justify-center ${
                  !selectedTeamId
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-black text-white hover:bg-gray-800"
                }`}
                disabled={!selectedTeamId}
                onClick={handleTeamRegisterAll}
              >
                <div className="mr-2">
                  <FaUsers />
                </div>
                팀 일괄 등록
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* 사용자 추가 모달 끗!!!!!! */}

      {/* 비밀번호 변경 모달 시작 !!! */}
      <div className={`${passwordModal ? "" : "hidden"}`}>
        <div className="font-[&#39;Noto_Sans_KR&#39;]">
          <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-60 backdrop-blur-sm"></div>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex items-center justify-center min-h-full p-4 text-center sm:p-0">
              <div className="relative px-6 pt-6 pb-6 overflow-hidden text-left transition-all transform bg-white shadow-2xl rounded-xl sm:my-8 sm:w-full sm:max-w-lg">
                <div className="absolute top-0 right-0 pt-4 pr-4">
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500"
                    onClick={() => {
                      handlePasswordModal();
                    }}
                  >
                    <span className="sr-only">닫기</span>
                    <IoCloseSharp size={20} />
                  </button>
                </div>

                <div className="sm:flex sm:items-start">
                  <div className="w-full mt-3 text-left sm:mt-0 sm:text-left">
                    <h3 className="mb-8 text-2xl font-bold leading-6 text-gray-900">비밀번호 변경</h3>
                  </div>
                </div>
                <div style={{ marginBottom: "15px" }}>
                  <label htmlFor="new-password" style={{ display: "block", marginBottom: "5px" }}>
                    새로운 비밀번호
                  </label>
                  <input
                    id="new-password"
                    type="text"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="새로운 비밀번호를 입력하세요"
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                    }}
                  />
                </div>
                <div style={{ marginBottom: "15px" }}>
                  <label htmlFor="confirm-password" style={{ display: "block", marginBottom: "5px" }}>
                    새로운 비밀번호 확인
                  </label>
                  <input
                    id="confirm-password"
                    type="text"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="새로운 비밀번호를 다시 입력하세요"
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                    }}
                  />
                </div>
                {error && (
                  <div
                    style={{
                      color: "red",
                      marginBottom: "15px",
                      fontSize: "14px",
                    }}
                  >
                    {error}
                  </div>
                )}
                <div className="gap-3 mt-8 sm:flex sm:justify-between">
                  <button
                    type="button"
                    className="inline-flex w-full justify-center items-center rounded-lg bg-white px-10 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto transition-all duration-200"
                    style={{
                      width: "48%", // 각 버튼이 반씩 차지하도록 설정
                    }}
                    onClick={() => {
                      handlePasswordModal();
                    }}
                  >
                    취소
                  </button>
                  <button
                    onClick={handlePasswordChange}
                    style={{
                      width: "48%", // 각 버튼이 반씩 차지하도록 설정
                      padding: "10px",
                      backgroundColor: "black",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                    }}
                  >
                    비밀번호 변경하기
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* 비밀번호 변경 모달 끝 !!!  */}

      {/* 사용자 상세 정보 모달 시작 !!! */}
      <div className={`${userDetailModal ? "" : "hidden"}`}>
        <div className="font-[&#39;Noto_Sans_KR&#39;]">
          <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-60 backdrop-blur-sm" onClick={handleUserDetailModal}></div>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex items-center justify-center min-h-full p-4 text-center sm:p-0">
              <div className="relative px-6 pt-6 pb-6 overflow-hidden text-left transition-all transform bg-white shadow-2xl rounded-xl sm:my-8 sm:w-full sm:max-w-4xl" onClick={(e) => e.stopPropagation()}>
                <div className="absolute top-0 right-0 pt-4 pr-4">
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500"
                    onClick={handleUserDetailModal}
                  >
                    <span className="sr-only">닫기</span>
                    <IoCloseSharp size={20} />
                  </button>
                </div>

                <div className="sm:flex sm:items-start">
                  <div className="w-full mt-3 text-left sm:mt-0 sm:text-left">
                    <h3 className="mb-6 text-2xl font-bold leading-6 text-gray-900">사용자 상세 정보</h3>
                  </div>
                </div>

                {loadingDetail ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-gray-500">로딩 중...</div>
                  </div>
                ) : userDetail ? (
                  <div className="space-y-6">
                    {/* 기본 정보 */}
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="mb-4 text-lg font-semibold text-gray-900">기본 정보</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-gray-500">사용자 ID</label>
                          <div className="mt-1 text-sm text-gray-900">{userDetail.username}</div>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500">닉네임</label>
                          <div className="mt-1 text-sm text-gray-900">{userDetail.nickname}</div>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500">이메일</label>
                          <div className="mt-1 text-sm text-gray-900">{userDetail.email}</div>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500">최종 로그인</label>
                          <div className="mt-1 text-sm text-gray-900">
                            {userDetail.lastLogin ? dayjs(new Date(userDetail.lastLogin)).format("YYYY-MM-DD HH:mm") : "없음"}
                          </div>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500">계정 생성일</label>
                          <div className="mt-1 text-sm text-gray-900">
                            {userDetail.createAt ? dayjs(new Date(userDetail.createAt)).format("YYYY-MM-DD HH:mm") : "없음"}
                          </div>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500">상태</label>
                          <div className="mt-1 text-sm text-gray-900">
                            {userDetail.deleteAt === 0 ? (
                              <span className="px-2 py-1 text-xs font-semibold text-green-800 bg-green-100 rounded-full">활성화</span>
                            ) : (
                              <span className="px-2 py-1 text-xs font-semibold text-red-800 bg-red-100 rounded-full">비활성화</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 팀 정보 */}
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="mb-4 text-lg font-semibold text-gray-900">소속 팀 ({userDetail.teams.length}개)</h4>
                      {userDetail.teams.length > 0 ? (
                        <div className="space-y-2">
                          {userDetail.teams.map((team, index) => (
                            <div key={index} className="p-2 bg-white rounded border border-gray-200">
                              <div className="text-sm font-medium text-gray-900">{team.teamName}</div>
                              <div className="text-xs text-gray-500">팀 ID: {team.teamId}</div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500">소속된 팀이 없습니다.</div>
                      )}
                    </div>

                    {/* 파일 정보 */}
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="mb-4 text-lg font-semibold text-gray-900">
                        파일 정보 ({userDetail.files.length}개, 총 용량: {formatFileSize(userDetail.totalFileSize)})
                      </h4>
                      {userDetail.files.length > 0 ? (
                        <div className="max-h-64 overflow-y-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="sticky top-0 bg-gray-100">
                              <tr>
                                <th className="px-4 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">파일명</th>
                                <th className="px-4 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">확장자</th>
                                <th className="px-4 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">크기</th>
                                <th className="px-4 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">팀</th>
                                <th className="px-4 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">생성일</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {userDetail.files.map((file) => (
                                <tr key={file.id}>
                                  <td className="px-4 py-2 text-sm text-gray-900">{file.name}</td>
                                  <td className="px-4 py-2 text-sm text-gray-500">{file.extension || "-"}</td>
                                  <td className="px-4 py-2 text-sm text-gray-500">{formatFileSize(file.size)}</td>
                                  <td className="px-4 py-2 text-sm text-gray-500">{file.teamName}</td>
                                  <td className="px-4 py-2 text-sm text-gray-500">
                                    {file.createAt ? dayjs(new Date(file.createAt)).format("YYYY-MM-DD HH:mm") : "-"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500">업로드한 파일이 없습니다.</div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-gray-500">사용자 정보를 불러올 수 없습니다.</div>
                  </div>
                )}

                <div className="gap-3 mt-8 sm:flex sm:justify-end">
                  <button
                    type="button"
                    className="inline-flex w-full justify-center items-center rounded-lg bg-white px-10 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto transition-all duration-200"
                    onClick={handleUserDetailModal}
                  >
                    닫기
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* 사용자 상세 정보 모달 끝 !!! */}
    </div>
  );
};

export default UserList;
